/**
 * Worker submissions and adjustments awaiting a staff decision, per trade
 * (PB-14).
 *
 * The order page counted only the trade it had open, and the orders list
 * counted nothing, so a decision waiting on another trade never reached the
 * recommendation. These are the same two sources the panels show —
 * `workerSubmissions` ("submitted", or a reimbursement still to pay) and
 * `jobs/{id}/adjustments` ("pending") — read for every trade at once.
 *
 * A source that has not answered, or failed, makes its trades UNKNOWN, never
 * zero: the progress derivation then refuses to say "nothing to do".
 */

import type { WorkerSubmission } from '@rendimees24/shared-types';
import { collection, collectionGroup, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { getFirebase } from './firebase';

export interface PendingReports {
  counts: Record<string, number>;
  /** Trades whose state is not known yet (loading or failed). */
  unknown: Set<string>;
}

function awaitsStaff(s: Pick<WorkerSubmission, 'status' | 'reimbursement'>): boolean {
  return s.status === 'submitted' || s.reimbursement?.status === 'pending';
}

/** Firestore `in` takes at most 30 values. */
function chunks<T>(xs: T[], n = 30): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

/** Pending reports for the given trades of one order. */
export function useOrderPendingReports(jobIds: string[]): PendingReports {
  const key = [...new Set(jobIds)].sort().join(',');
  const [subs, setSubs] = useState<Map<number, Record<string, number>> | null>(null);
  const [subsFailed, setSubsFailed] = useState(false);
  const [adj, setAdj] = useState<Record<string, number | null>>({});

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    setSubs(null);
    setSubsFailed(false);
    setAdj({});
    if (ids.length === 0) return;
    const { firestore } = getFirebase();
    const parts = chunks(ids);
    const byChunk = new Map<number, Record<string, number>>();
    const unsubs = parts.map((part, i) =>
      onSnapshot(
        query(collection(firestore, 'workerSubmissions'), where('jobId', 'in', part)),
        (snap) => {
          const c: Record<string, number> = {};
          for (const d of snap.docs) {
            const s = d.data() as WorkerSubmission;
            if (awaitsStaff(s)) c[s.jobId] = (c[s.jobId] ?? 0) + 1;
          }
          byChunk.set(i, c);
          if (byChunk.size === parts.length) setSubs(new Map(byChunk));
        },
        () => setSubsFailed(true),
      ),
    );
    for (const id of ids) {
      unsubs.push(
        onSnapshot(
          query(collection(firestore, 'jobs', id, 'adjustments'), where('status', '==', 'pending')),
          (snap) => setAdj((prev) => ({ ...prev, [id]: snap.size })),
          () => setAdj((prev) => ({ ...prev, [id]: null })),
        ),
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [key]);

  return useMemo(() => {
    const ids = key ? key.split(',') : [];
    const counts: Record<string, number> = {};
    const unknown = new Set<string>();
    const merged: Record<string, number> = {};
    for (const c of subs?.values() ?? []) for (const [id, n] of Object.entries(c)) merged[id] = n;
    for (const id of ids) {
      const a = adj[id];
      if (!subs || subsFailed || a === undefined || a === null) unknown.add(id);
      else counts[id] = (merged[id] ?? 0) + a;
    }
    return { counts, unknown };
  }, [key, subs, subsFailed, adj]);
}

/**
 * Pending reports across every order, for the orders list: two queries for
 * submissions and one collection-group query for adjustments (staff-only rule
 * `/{path=**}/adjustments`). `known` stays false until all three answered.
 */
export function useAllPendingReports(): { counts: Record<string, number>; known: boolean } {
  const [submitted, setSubmitted] = useState<WorkerSubmission[] | null>(null);
  const [reimburse, setReimburse] = useState<WorkerSubmission[] | null>(null);
  const [adjustments, setAdjustments] = useState<Record<string, number> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const { firestore } = getFirebase();
    const fail = () => setFailed(true);
    const rows = (snap: { docs: { id: string; data: () => unknown }[] }) =>
      snap.docs.map((d) => ({ ...(d.data() as WorkerSubmission), id: d.id }));
    const unsubs = [
      onSnapshot(
        query(collection(firestore, 'workerSubmissions'), where('status', '==', 'submitted')),
        (s) => setSubmitted(rows(s)),
        fail,
      ),
      onSnapshot(
        query(
          collection(firestore, 'workerSubmissions'),
          where('reimbursement.status', '==', 'pending'),
        ),
        (s) => setReimburse(rows(s)),
        fail,
      ),
      onSnapshot(
        query(collectionGroup(firestore, 'adjustments'), where('status', '==', 'pending')),
        (s) => {
          const c: Record<string, number> = {};
          for (const d of s.docs) {
            // jobs/{jobId}/adjustments/{id}
            const jobId = d.ref.parent.parent?.id;
            if (jobId) c[jobId] = (c[jobId] ?? 0) + 1;
          }
          setAdjustments(c);
        },
        fail,
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  return useMemo(() => {
    const counts: Record<string, number> = { ...(adjustments ?? {}) };
    // One submission can be both "submitted" and awaiting reimbursement.
    const seen = new Set<string>();
    for (const s of [...(submitted ?? []), ...(reimburse ?? [])]) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      counts[s.jobId] = (counts[s.jobId] ?? 0) + 1;
    }
    return { counts, known: !failed && !!submitted && !!reimburse && !!adjustments };
  }, [submitted, reimburse, adjustments, failed]);
}
